import React, { useState } from 'react';

import Button from 'components/form/Button.component';
import DropdownInput from 'components/form/DropdownInput.component';
import { MultiSelectDropdownInput } from 'components/form/MultiSelectDropdownInput.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import useTip from 'hooks/useTip.hook';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';

import CN from './UiPage.module.scss'; // Since this is experiment, we can share the same .module.scss file

const content = (
  <div className={CN.dropdownContent}>
    <div className={CN.dropdownItem}>Banana</div>
    <div className={CN.dropdownItem}>Apple</div>
    <div className={CN.dropdownItem}>Raspberries</div>
  </div>
);

const fruitOptions = [
  { description: 'Banana', extra: 'banana' },
  { description: 'Apple', extra: 'apple' },
  { description: 'Raspberries', extra: 'raspberries' },
  { description: 'Mango', extra: 'mango' },
  { description: 'Pineapple', extra: 'pineapple' },
  { description: 'Blueberry', extra: 'blueberry' },
];

const DropdownPanelUi = () => {
  const { val: isTopOpen, tip: toggleTop } = useTip(false);
  const { val: isBottomOpen, tip: toggleBottom } = useTip(false);
  const { val: isBottomRightOpen, tip: toggleBottomRight } = useTip(false);

  const { val: isSmOpen, tip: toggleSm } = useTip(false);
  const { val: isLgOpen, tip: toggleLg } = useTip(false);
  const { val: isMaxOpen, tip: toggleMax } = useTip(false);
  const { val: isFullOpen, tip: toggleFull } = useTip(false);
  const { val: isTabletPOpen, tip: toggleTabletP } = useTip(false);

  const { val: isWithFooterOpen, tip: toggleWithFooter } = useTip(false);
  const { val: isWithHeaderOpen, tip: toggleWithHeader } = useTip(false);
  const { val: isWithAllOpen, tip: toggleWithAll } = useTip(false);

  const [dropdownInputValue, setDropdownInputValue] = useState(null);
  const [multiSelectValue, setMultiSelectValue] = useState([]);

  return (
    <div data-bem="DropdownPanelUi" className={CN.container}>
      <div className={CN.title}>
        <code>
          position = bottom | bottomRight | top | <i>and other custom...</i>
        </code>
      </div>
      {/* POSITION options */}
      <div className={CN.examples}>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleBottom} tag="">
            BOTTOM
          </Button>
          {isBottomOpen && (
            <DropdownFoldout width="sm" position="bottom" onClickOutside={toggleBottom}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>

        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleBottomRight} tag="">
            BOTTOM RIGHT
          </Button>
          {isBottomRightOpen && (
            <DropdownFoldout width="sm" position="bottomRight" onClickOutside={toggleBottomRight}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleTop} tag="">
            TOP
          </Button>
          {isTopOpen && (
            <DropdownFoldout width="sm" position="top" onClickOutside={toggleTop}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
      </div>

      {/* WIDTH options */}
      <div className={CN.title}>
        <code> width = sm | lg | max | maxInTabletP | full</code>
        <p>max is full width on mobile</p>
        <p>maxInTabletP is full width on tablet portrait and below</p>
        <p>full - in all resolutions</p>
      </div>
      <div className={CN.examplesForWidth}>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleSm} tag="">
            SM
          </Button>
          {isSmOpen && (
            <DropdownFoldout width="sm" onClickOutside={toggleSm}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleLg} tag="">
            LG
          </Button>
          {isLgOpen && (
            <DropdownFoldout width="lg" onClickOutside={toggleLg}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleMax} tag="" onClickOutside={toggleMax}>
            MAX
          </Button>
          {isMaxOpen && (
            <DropdownFoldout width="max" onClickOutside={toggleMax}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleTabletP} tag="" onClickOutside={toggleTabletP}>
            MAX IN TABLET P
          </Button>
          {isTabletPOpen && (
            <DropdownFoldout width="maxInTabletP" onClickOutside={toggleTabletP}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleFull} tag="" onClickOutside={toggleFull}>
            FULL
          </Button>
          {isFullOpen && (
            <DropdownFoldout width="full" onClickOutside={toggleFull}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
      </div>

      {/* VARIANT content */}
      <div className={CN.title}>
        <code>
          content variant = withFooter | withHeader | withAll | panel <i>custom</i>
        </code>
      </div>
      <div className={CN.examples}>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleWithFooter} tag="">
            WITH FOOTER
          </Button>
          {isWithFooterOpen && (
            <DropdownFoldout width="lg" variant="withFooter" onClickOutside={toggleWithFooter}>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
              <DropdownFoldout.Footer>
                <Button variant="primary" onClick={toggleWithFooter} tag="">
                  CLOSE
                </Button>
              </DropdownFoldout.Footer>
            </DropdownFoldout>
          )}
        </div>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleWithHeader} tag="">
            WITH HEADER
          </Button>
          {isWithHeaderOpen && (
            <DropdownFoldout width="lg" variant="withHeader" onClickOutside={toggleWithHeader}>
              <DropdownFoldout.Header>Fruit list</DropdownFoldout.Header>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
            </DropdownFoldout>
          )}
        </div>
        <div className={CN.dropdownContainer}>
          <Button variant="outline" onClick={toggleWithAll} tag="">
            WITH FOOTER AND HEADER
          </Button>
          {isWithAllOpen && (
            <DropdownFoldout width="lg" variant="withAll" onClickOutside={toggleWithAll}>
              <DropdownFoldout.Header>
                <div className={CN.dropdownTitle}>Fruit list</div>
              </DropdownFoldout.Header>
              <DropdownFoldout.Body>{content}</DropdownFoldout.Body>
              <DropdownFoldout.Footer>
                <Button variant="primary" onClick={toggleWithAll} tag="">
                  CLOSE
                </Button>
              </DropdownFoldout.Footer>
            </DropdownFoldout>
          )}
        </div>
      </div>

      {/* DropdownInput component */}
      <div className={CN.title} style={{ margin: '16px 0' }}>
        <code>DropdownInput — select single value</code>
      </div>
      <div className={CN.examples}>
        <div className={CN.dropdownContainer}>
          <code>default</code>
          <DropdownInput
            name="fruit-default"
            options={fruitOptions}
            value={dropdownInputValue}
            placeholder="Select a fruit..."
            onChange={({ raw }) => setDropdownInputValue({ description: raw, extra: raw })}
          />
        </div>
        <div className={CN.dropdownContainer}>
          <code>disabled</code>
          <DropdownInput name="fruit-disabled" options={fruitOptions} value={{ description: 'Apple', extra: 'apple' }} disabled />
        </div>
        <div className={CN.dropdownContainer}>
          <code>isButtonLargeHeight</code>
          <DropdownInput name="fruit-large" options={fruitOptions} placeholder="Large height" isButtonLargeHeight />
        </div>
        <div className={CN.dropdownContainer}>
          <code>align=&quot;right&quot;</code>
          <DropdownInput name="fruit-right" options={fruitOptions} placeholder="Right aligned list" align="right" />
        </div>
      </div>

      {/* MultiSelectDropdownInput component */}
      <div className={CN.title} style={{ margin: '16px 0' }}>
        <code>MultiSelectDropdownInput — select multiple values (max 4)</code>
      </div>
      <div className={CN.examples}>
        <div className={CN.dropdownContainer}>
          <code>default</code>
          <MultiSelectDropdownInput name="multi-fruit-default" options={fruitOptions} value={multiSelectValue} placeholder="Select fruits..." onChange={({ value }) => setMultiSelectValue(value)} />
        </div>
        <div className={CN.dropdownContainer}>
          <code>preselected</code>
          <MultiSelectDropdownInput name="multi-fruit-preselected" options={fruitOptions} value={['banana', 'mango']} placeholder="Preselected" onChange={alwaysNoop} />
        </div>
        <div className={CN.dropdownContainer}>
          <code>maxSelections=2</code>
          <MultiSelectDropdownInput name="multi-fruit-max2" options={fruitOptions} value={[]} maxSelections={2} placeholder="Max 2 selections" onChange={alwaysNoop} />
        </div>
        <div className={CN.dropdownContainer}>
          <code>disabled</code>
          <MultiSelectDropdownInput name="multi-fruit-disabled" options={fruitOptions} value={['apple', 'raspberries']} placeholder="Disabled" disabled onChange={alwaysNoop} />
        </div>
      </div>
    </div>
  );
};

export default DropdownPanelUi;
